import { ArrowUpRight, Cog, Wrench, Layers } from "lucide-react";
import { useInView, useCountUp } from "../hooks/useIntersectionObserver";
import Reveal from "./Reveal";
import { tc, tb, tbr } from "../lib/themeStyles";

const HERO_IMG =
  "https://images.unsplash.com/photo-1711418235334-8895331a6cf9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function BentoCard({ children, className = "", delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${inView ? "in-view" : ""} group relative overflow-hidden card-surface ${className}`}
    >
      {children}
    </div>
  );
}

function Stat({ value, decimals = 0, suffix = "", prefix = "", label, className = "", cellStyle = {} }) {
  const [ref, formatted] = useCountUp(value, { decimals });
  return (
    <div
      ref={ref}
      className={`px-6 py-10 md:py-12 text-center md:text-left ${className}`}
      style={cellStyle}
    >
      <div
        className="text-[36px] md:text-[56px] font-extrabold tracking-[-0.03em] leading-none"
        style={tc.primary}
      >
        {prefix}
        {formatted}
        {suffix}
      </div>
      <div className="mt-3 label-cap" style={tc.muted}>
        {label}
      </div>
    </div>
  );
}

export default function BentoCapabilities() {
  return (
    <section id="capabilities" className="section-shell">
      <div className="section-inner">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="label-cap mb-4" style={tc.accent}>
              01 — Capabilities
            </div>
            <h2 className="section-title max-w-[720px]">
              A vertically integrated factory, engineered for global supply chains.
            </h2>
          </div>
          <p className="text-[15px] max-w-md leading-[1.7]" style={tc.muted}>
            From raw sheet stock to finished sub-assemblies — every operation under one roof in Pune,
            India.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          <BentoCard className="min-h-[380px] lg:min-h-[440px]">
            <div className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity duration-500">
              <img
                src={HERO_IMG}
                alt="CNC precision metal manufacturing at Malunjkar Engineering Pune India"
                width={1200}
                height={800}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-tr from-[#05050f] via-[#05050f]/75 to-transparent" />
            <div className="relative h-full min-h-[380px] lg:min-h-[440px] p-8 md:p-10 flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <Layers size={18} style={tc.accent} />
                <span className="label-cap" style={tc.accent}>
                  Sheet Metal & Blanking
                </span>
              </div>
              <div>
                <h3
                  className="text-[26px] md:text-[34px] font-bold tracking-[-0.02em] leading-[1.05] max-w-[480px]"
                  style={tc.primary}
                >
                  CNC Fiber Laser. Press Brake. Turret Punching.
                </h3>
                <p className="mt-3 text-[15px] max-w-[400px] leading-[1.7]" style={tc.muted}>
                  High-speed sheet processing with repeatable accuracy across mild steel, stainless,
                  and aluminum.
                </p>
                <div
                  className="mt-5 inline-flex items-center gap-2 text-[14px] font-medium"
                  style={tc.primary}
                >
                  Explore process
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
              </div>
            </div>
          </BentoCard>

          <div className="flex flex-col gap-5 md:gap-6">
            <BentoCard className="flex-1 min-h-[200px]" delay={80}>
              <div className="h-full min-h-[200px] p-8 flex flex-col justify-between">
                <Cog size={22} style={tc.accent} />
                <div>
                  <h3 className="text-[22px] font-semibold" style={tc.primary}>
                    Precision Machining
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed" style={tc.muted}>
                    VMC · Turning · Boring — ±0.005mm
                  </p>
                </div>
              </div>
            </BentoCard>
            <BentoCard className="flex-1 min-h-[200px]" delay={140}>
              <div className="h-full min-h-[200px] p-8 flex flex-col justify-between">
                <Wrench size={22} style={tc.accent} />
                <div>
                  <h3 className="text-[22px] font-semibold" style={tc.primary}>
                    Fabrication & Assembly
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed" style={tc.muted}>
                    MIG/TIG · 10T Crane · Custom Jigs
                  </p>
                </div>
              </div>
            </BentoCard>
          </div>
        </div>

        <Reveal
          delay={200}
          className="mt-6 rounded-2xl border overflow-hidden"
          style={{ ...tb.surface, ...tbr.default }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4">
            <Stat
              value={25000}
              suffix="+"
              label="sq ft floor space"
              className="border-b lg:border-b-0 lg:border-r"
              cellStyle={tbr.default}
            />
            <Stat
              value={0.005}
              decimals={3}
              prefix="±"
              suffix="mm"
              label="tolerance"
              className="border-b lg:border-b-0 lg:border-r"
              cellStyle={tbr.default}
            />
            <Stat
              value={15}
              suffix="+ yrs"
              label="experience"
              className="border-b lg:border-b-0 lg:border-r"
              cellStyle={tbr.default}
            />
            <Stat value={30} suffix="+" label="export countries" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
